import { useEffect, useState } from "react";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Star,
  X,
  FileText,
  BriefcaseBusiness,
  MessageSquareQuote,
  UserRound,
  Crown,
} from "lucide-react";
import { getUserById } from "../../services/authService";
import { getUserEvaluations, getEvaluationSummary } from "../../services/evaluationService";
import "./FreelancerViewCompanyModal.scss";

const IconStar = ({ filled }) => (
  <Star
    size={15}
    strokeWidth={1.5}
    fill={filled ? "currentColor" : "none"}
  />
);

export default function FreelancerViewCompanyModal({
  companyId,
  onClose,
}) {
  const [profile, setProfile] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [profileData, evaluationsData, summaryData] =
          await Promise.all([
            getUserById(companyId),
            getUserEvaluations(companyId),
            getEvaluationSummary(companyId),
          ]);
        setProfile(profileData);
        setEvaluations(evaluationsData);
        setSummary(summaryData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    if (companyId) load();
  }, [companyId]);

  const averageRating =
    evaluations.length > 0
      ? evaluations.reduce((s, e) => s + e.rating, 0) / evaluations.length
      : 0;

  const displayName = profile?.name || "Empresa";
  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((name) => name[0])
    .join("")
    .toUpperCase();

  // Verifica se a empresa é gold (pelo summary.badge ou propriedade do profile)
  const isGold = summary?.badge?.toLowerCase().includes("gold") || profile?.isGold || profile?.plan === "gold";

  return (
    <div className="fvc-overlay" onClick={onClose}>
      <div
        className={`fvc-modal ${isGold ? "fvc-modal--gold" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="fvc-close"
          onClick={onClose}
          aria-label="Fechar"
          type="button"
        >
          <X size={18} />
        </button>

        {loading && (
          <div className="fvc-loading">
            <div className="fvc-loading-spinner" />
            <span>Carregando perfil...</span>
          </div>
        )}

        {!loading && profile && (
          <div className="fvc-body">
            {/* ================= HEADER ================= */}
            <div className={`fvc-header ${isGold ? "fvc-header--gold" : ""}`}>
              <div className="fvc-header-content">
                <div className="fvc-header-left">
                  <div className={`fvc-avatar-wrapper ${isGold ? "fvc-avatar-wrapper--gold" : ""}`}>
                    {profile.avatarUrl ? (
                      <img
                        src={profile.avatarUrl}
                        alt={displayName}
                        className="fvc-avatar"
                      />
                    ) : (
                      <div className="fvc-avatar-fallback">{initials}</div>
                    )}
                    {isGold && (
                      <div className="fvc-gold-crown-badge" title="Empresa Gold">
                        <Crown size={12} />
                      </div>
                    )}
                  </div>

                  <div className="fvc-header-info">
                    <h1 className="fvc-name">
                      {displayName}
                      {isGold && <span className="fvc-gold-tag">Gold</span>}
                    </h1>
                    <div className="fvc-meta">
                      <span className="fvc-type">
                        <Building2 size={14} />
                        Empresa
                      </span>
                      {summary?.badge && (
                        <span className={`fvc-badge ${isGold ? "fvc-badge--gold" : ""}`}>
                          {summary.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="fvc-header-right">
                  <div className="fvc-rating-box">
                    <div className="fvc-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <IconStar
                          key={star}
                          filled={star <= Math.round(averageRating)}
                        />
                      ))}
                    </div>
                    <span className="fvc-rating-value">
                      {evaluations.length > 0 ? averageRating.toFixed(1) : "—"}
                    </span>
                    <span className="fvc-rating-label">
                      {evaluations.length > 0
                        ? `${evaluations.length} avaliações`
                        : "Sem avaliações"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= STATS ================= */}
            <div className="fvc-stats">
              <div className="fvc-stat">
                <span className="fvc-stat-value">
                  {evaluations.length > 0 ? averageRating.toFixed(1) : "—"}
                </span>
                <span className="fvc-stat-label">Média</span>
              </div>
              <div className="fvc-stat">
                <span className="fvc-stat-value">{evaluations.length}</span>
                <span className="fvc-stat-label">Avaliações</span>
              </div>
              <div className="fvc-stat">
                <span className="fvc-stat-value">
                  {profile.freelancers?.length || 0}
                </span>
                <span className="fvc-stat-label">Freelancers</span>
              </div>
            </div>

            {/* ================= CONTENT ================= */}
            <div className="fvc-content">
              {profile.bio && (
                <section className="fvc-section">
                  <div className="fvc-section-header">
                    <FileText size={16} className="fvc-section-icon" />
                    <h3>Sobre a empresa</h3>
                  </div>
                  <p className="fvc-bio">{profile.bio}</p>
                </section>
              )}

              {profile.address && (
                <section className="fvc-section">
                  <div className="fvc-section-header">
                    <BriefcaseBusiness size={16} className="fvc-section-icon" />
                    <h3>Informações de contato</h3>
                  </div>
                  <div className="fvc-contact-grid">
                    <div className="fvc-contact-item fvc-contact-item--address">
                      <MapPin size={16} />
                      <div className="fvc-address-details">
                        {profile.address.logradouro && <span>{profile.address.logradouro}</span>}
                        {profile.address.numero && <span>, {profile.address.numero}</span>}
                        {profile.address.bairro && <span> - {profile.address.bairro}</span>}
                        {profile.address.cidade && <span> - {profile.address.cidade}</span>}
                        {profile.address.estado && <span> - {profile.address.estado}</span>}
                        {profile.address.cep && <span> - CEP: {profile.address.cep}</span>}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section className="fvc-section fvc-section--last">
                <div className="fvc-section-header">
                  <MessageSquareQuote size={16} className="fvc-section-icon" />
                  <h3>Avaliações de freelancers</h3>
                  <span className="fvc-section-count">
                    {evaluations.length} avaliações
                  </span>
                </div>

                {evaluations.length > 0 ? (
                  <div className="fvc-evaluations">
                    {evaluations.map((evaluation, index) => (
                      <div key={index} className="fvc-eval-item">
                        <div className="fvc-eval-top">
                          <div className="fvc-eval-user">
                            <UserRound size={14} />
                            <span>{evaluation.evaluatorName}</span>
                          </div>
                          <div className="fvc-eval-stars">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <IconStar
                                key={star}
                                filled={star <= evaluation.rating}
                              />
                            ))}
                          </div>
                        </div>
                        {evaluation.comment && (
                          <p className="fvc-eval-comment">
                            {evaluation.comment}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="fvc-empty">
                    <p>Esta empresa ainda não recebeu avaliações de freelancers.</p>
                  </div>
                )}
              </section>
            </div>
          </div>
        )}

        {!loading && !profile && (
          <div className="fvc-error">
            <Building2 size={32} />
            <strong>Erro ao carregar perfil</strong>
            <p>Tente novamente mais tarde.</p>
            <button onClick={onClose}>Fechar</button>
          </div>
        )}
      </div>
    </div>
  );
}